import DOMPurify from "dompurify";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Send, FlaskConical } from "lucide-react";

export const ComposeTab = ({ segments, compose, setCompose, recipientInfo, sending, onSendTest, onSend }) => {
  const { name, segmentId, subject, html } = compose;
  const patch = (p) => setCompose((c) => ({ ...c, ...p }));

  const previewHtml = DOMPurify.sanitize(
    (html || "<p style='color:#999'>Your message will appear here…</p>").replaceAll("{{first_name}}", "Sarah")
  );

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-5">
        <div className="space-y-1.5">
          <Label>Campaign name (for your records)</Label>
          <Input value={name} onChange={(e) => patch({ name: e.target.value })} placeholder="e.g. June newsletter" data-testid="campaign-name-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Send to</Label>
          <Select value={segmentId} onValueChange={(v) => patch({ segmentId: v })}>
            <SelectTrigger data-testid="campaign-segment-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              {segments.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.count})</SelectItem>)}
            </SelectContent>
          </Select>
          {recipientInfo && (
            <p className="text-xs text-muted-foreground" data-testid="recipient-count-info">
              {recipientInfo.count} people will receive this
              {recipientInfo.suppressed > 0 && ` · ${recipientInfo.suppressed} unsubscribed (skipped)`}
              {recipientInfo.no_email > 0 && ` · ${recipientInfo.no_email} have no email`}
            </p>
          )}
        </div>
        <div className="space-y-1.5">
          <Label>Subject line</Label>
          <Input value={subject} onChange={(e) => patch({ subject: e.target.value })} placeholder="A word of encouragement from Joanna" data-testid="campaign-subject-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Message</Label>
          <RichTextEditor value={html} onChange={(v) => patch({ html: v })} />
          <p className="text-xs text-muted-foreground">Use the "First name" button to greet each person by name.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={onSendTest} disabled={!subject.trim() || !html.trim()} data-testid="send-test-btn">
            <FlaskConical className="h-4 w-4 mr-2" /> Send me a test first
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button disabled={sending || !name.trim() || !subject.trim() || !html.trim() || !recipientInfo?.count} data-testid="send-campaign-btn">
                <Send className="h-4 w-4 mr-2" /> Send to {recipientInfo?.count ?? 0} people
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-serif">Send "{name}" to {recipientInfo?.count} people?</AlertDialogTitle>
                <AlertDialogDescription>
                  This sends "{subject}" to {recipientInfo?.segment_name}. Unsubscribed addresses are always skipped. We recommend sending yourself a test first.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Not yet</AlertDialogCancel>
                <AlertDialogAction onClick={onSend} data-testid="confirm-send-btn">Yes, send it</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card className="shadow-sm h-fit" data-testid="email-preview-card">
        <CardHeader className="pb-3"><CardTitle className="font-serif text-xl">Live preview</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border border-border bg-[#FBFAF6] p-6">
            <p className="text-sm font-semibold border-b border-border pb-3 mb-4">{subject || "Your subject line"}</p>
            <div className="text-sm leading-relaxed [&_a]:text-primary [&_ul]:list-disc [&_ul]:pl-5" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            <hr className="my-6 border-border" />
            <p className="text-xs text-muted-foreground">You are receiving this because you are part of the Joanna Beck Ministries family. <span className="underline">Unsubscribe</span></p>
          </div>
          <p className="text-xs text-muted-foreground mt-3">Shown with "Sarah" as the sample first name.</p>
        </CardContent>
      </Card>
    </div>
  );
};
